import { useQuery } from '@tanstack/react-query';
import { ApiError, requestJson } from './client';
import type { Person } from './org';

export interface EmployeeCard extends Person {
  city: string | null;
  status: 'active' | 'dismissed';
  departments: { id: string; name: string }[];
  managers: Person[];
  leads: { id: string; name: string; coHead: boolean }[];
  directSubordinates: number;
  /** Прямые подчинённые: раскрываются в карточке. */
  subordinates?: { id: string; fullName: string; position: string | null; photoUrl: string | null }[];
  canOpenCabinet: boolean;
  /** Раздел KPI открыт и без кабинета: администратору. */
  canReadKpi: boolean;
  /** Раздел «Посещения» открыт и без кабинета: администратору. */
  canReadAttendance: boolean;
  /** Удержания (как штрафы) или оклад. */
  canReadPayroll: boolean;
}

export interface EmployeeProfile {
  id: string;
  hiredOn: string | null;
  email: string | null;
  mobilePhone: string | null;
  workPhone: string | null;
  directSubordinates: number;
}

export interface Permission {
  action: string;
  label: string;
  allowed: boolean;
  reason: string;
}

export function useEmployeeCard(id: string | null) {
  return useQuery({
    queryKey: ['employee', id, 'card'],
    queryFn: () => requestJson<EmployeeCard>(`/api/v1/employees/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

/** Закрытый профиль; null — нет прав (403). */
export function useEmployeeProfile(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ['employee', id, 'profile'],
    queryFn: async () => {
      try {
        return await requestJson<EmployeeProfile>(`/api/v1/employees/${id}/profile`);
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) return null;
        throw error;
      }
    },
    enabled: Boolean(id) && enabled,
    staleTime: 60_000,
  });
}

export function usePermissions(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['employee', id, 'permissions'],
    queryFn: () => requestJson<{ items: Permission[] }>(`/api/v1/employees/${id}/permissions`),
    enabled: Boolean(id) && enabled,
  });
}
